import { memo, useEffect, useRef, useState } from 'react';
import { bankLogo, bankInitial } from '../data/bankLogos';

const STYLE = 'https://styles.maptoolkit.org/summer.json';
const LIB = 'https://cdn.jsdelivr.net/npm/maplibre-gl@6.9.0/dist/maplibre-gl.mjs';
const CSS = 'https://cdn.jsdelivr.net/npm/maplibre-gl@6.9.0/dist/maplibre-gl.css';
const KOREA = [[124.0, 32.8], [132.2, 39.0]];
const CELL = 80; // 클러스터 격자 크기(px): 핀+금리 라벨이 이웃 칸과 겹치지 않는 크기

let libraryPromise;
// 지도 라이브러리는 앱 시작 시점에 미리 받아 둡니다 (index.js에서 호출).
export function preloadMap() {
  if (!libraryPromise) libraryPromise = import(/* webpackIgnore: true */ LIB);
  if (!document.querySelector('[data-maplibre-css]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = CSS; link.dataset.maplibreCss = 'true';
    document.head.appendChild(link);
  }
  return libraryPromise;
}

function viewport(map, programmatic) {
  const b = map.getBounds(); const c = map.getCenter();
  return { center: [c.lat, c.lng], bounds: [b.getSouth(), b.getWest(), b.getNorth(), b.getEast()], zoom: map.getZoom(), programmatic };
}

// 화면 이동(팬)과 무관하게 같은 묶음이 되도록 세계 좌표(메르카토르 픽셀) 격자로 묶습니다.
function worldPx(lng, lat, zoom) {
  const scale = 512 * 2 ** zoom;
  const s = Math.sin((lat * Math.PI) / 180);
  return [((lng + 180) / 360) * scale, (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * scale];
}
function cluster(points, zoom, activeId) {
  const cells = new Map();
  const z = Math.floor(zoom);
  for (const p of points) {
    if (p.id === activeId) continue;
    const [x, y] = worldPx(p.lng, p.lat, z);
    const key = `${z}:${Math.floor(x / CELL)}:${Math.floor(y / CELL)}`;
    if (!cells.has(key)) cells.set(key, []);
    cells.get(key).push(p);
  }
  return cells;
}

const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };

// 마커 바깥 틀(.mk-anchor)은 지도 라이브러리가 위치를 옮기는 요소라 스타일을 두지 않고,
// 모양과 효과는 안쪽 요소에만 적용합니다. (확대·축소 시 마커가 밀리지 않도록)
function pinElement(place, active, radarMode) {
  const root = el('div', 'mk-anchor');
  const radar = radarMode ? place.radar : null;
  const rate = radar ? radar.rate : place.product?.rate;
  const kind = place.isAtm ? 'atm' : rate != null ? 'rate' : 'plain';
  const btn = el('button', `pin pin--${kind}${radar ? ' pin--radar' : ''}${radarMode && !radar ? ' is-dim' : ''}${active ? ' is-active' : ''}`);
  btn.type = 'button';
  btn.dataset.id = place.id;
  btn.title = place.name;
  btn.setAttribute('aria-label', rate != null ? `${place.name} ${rate.toFixed(2)}%` : place.name);
  if (kind === 'atm') btn.appendChild(el('span', 'pin-dot'));
  else {
    const ring = el('span', 'pin-ring');
    const av = el('span', 'pin-avatar');
    const logo = bankLogo(`${place.bank || ''} ${place.name || ''}`);
    if (logo) { const img = el('img'); img.src = logo; img.alt = ''; img.loading = 'lazy'; img.onerror = () => { img.remove(); av.textContent = bankInitial(place.bank || place.name); }; av.appendChild(img); }
    else av.textContent = bankInitial(place.bank || place.name);
    ring.appendChild(av);
    btn.appendChild(ring);
    if (radar) ring.appendChild(el('span', 'pin-flag', '창구'));
    if (kind === 'rate') btn.appendChild(el('span', 'pin-label', `${rate.toFixed(2)}%`));
  }
  root.appendChild(btn);
  return root;
}
function clusterElement(group, radarMode) {
  const root = el('div', 'mk-anchor');
  const rateOf = p => (radarMode ? p.radar?.rate : p.product?.rate);
  const best = group.reduce((m, p) => (rateOf(p) != null && (m == null || rateOf(p) > m) ? rateOf(p) : m), null);
  const btn = el('button', `pin-cluster${best != null ? ' has-rate' : ''}${radarMode && best != null ? ' has-radar' : ''}${radarMode && best == null ? ' is-dim' : ''}`);
  btn.type = 'button';
  btn.setAttribute('aria-label', `${group.length}개 지점 확대`);
  btn.appendChild(el('b', null, String(group.length)));
  if (best != null) btn.appendChild(el('small', null, `${radarMode ? '창구' : '최고'} ${best.toFixed(2)}%`));
  root.appendChild(btn);
  return root;
}

function MaptoolkitMap({ center, focus, focusPoint, branches = [], atms = [], showAtms = false, activeId, hoverId, radarMode = false, heat = null, onSelect, onViewportChange, onMapClick, onHeatClick }) {
  const box = useRef(null);
  const map = useRef(null);
  const lib = useRef(null);
  const markers = useRef(new Map()); // key -> { marker, sig }
  const callbacks = useRef({ onSelect, onViewportChange, onMapClick, onHeatClick });
  callbacks.current = { onSelect, onViewportChange, onMapClick, onHeatClick };
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(13);

  useEffect(() => {
    let cancelled = false;
    preloadMap().then(l => {
      if (cancelled || !box.current) return;
      lib.current = l;
      const instance = new l.Map({
        container: box.current, style: STYLE, center: [center[1], center[0]], zoom: 13,
        minZoom: 6, maxZoom: 18, maxBounds: KOREA, renderWorldCopies: false,
        dragRotate: false, pitchWithRotate: false, touchPitch: false, maxPitch: 0, fadeDuration: 0, attributionControl: false,
      });
      instance.touchZoomRotate.disableRotation();
      map.current = instance;
      instance.on('load', () => { setReady(true); callbacks.current.onViewportChange?.(viewport(instance, true)); });
      // 앱이 직접 옮긴 이동(programmatic)은 '이 지역 검색' 기준점으로만 쓰입니다.
      instance.on('moveend', e => callbacks.current.onViewportChange?.(viewport(instance, Boolean(e.programmatic))));
      // 클러스터는 정수 확대 단계가 바뀔 때만 다시 계산합니다 (팬·소수 확대는 그대로).
      instance.on('zoomend', () => setZoomLevel(Math.floor(instance.getZoom())));
      instance.on('click', e => {
        const hit = instance.getLayer('heat-fill') && instance.queryRenderedFeatures(e.point, { layers: ['heat-fill'] })[0];
        if (hit && callbacks.current.onHeatClick) callbacks.current.onHeatClick({ ...hit.properties, lat: e.lngLat.lat, lng: e.lngLat.lng });
        else callbacks.current.onMapClick?.();
      });
      instance.on('error', e => { if (!instance.loaded() && e?.error) setError(true); });
    }).catch(() => setError(true));
    const all = markers.current;
    return () => { cancelled = true; all.forEach(m => m.marker.remove()); all.clear(); map.current?.remove(); map.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ready || !map.current || !focus) return;
    if (focus.bounds) {
      const [s, w, n, e] = focus.bounds;
      map.current.fitBounds([[w, s], [e, n]], { padding: { top: 96, bottom: 72, left: 48, right: 48 }, maxZoom: 15.5, duration: 600 }, { programmatic: true });
    } else map.current.flyTo({ center: [focus.center[1], focus.center[0]], zoom: 14 }, { programmatic: true });
  }, [focus?.key, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ready || !map.current || !focusPoint) return;
    map.current.easeTo({ center: [focusPoint.lng, focusPoint.lat], zoom: Math.max(map.current.getZoom(), 16), duration: 600, offset: [0, -60] }, { programmatic: true });
  }, [focusPoint?.key, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // 마커 동기화: 바뀐 것만 새로 만들고 나머지는 그대로 둡니다.
  useEffect(() => {
    if (!ready || !map.current || !lib.current) return;
    const points = [...branches.map(p => ({ ...p, isAtm: false })), ...(showAtms ? atms.map(p => ({ ...p, isAtm: true })) : [])];
    const wanted = new Map();
    for (const [key, group] of cluster(points, zoomLevel, activeId)) {
      if (group.length === 1) { const p = group[0]; wanted.set(`p:${p.id}`, { kind: 'pin', place: p, sig: `${p.product?.id || ''}:${p.product?.rate ?? ''}:${radarMode ? p.radar?.id || '-' : ''}:0` }); }
      else wanted.set(`c:${key}`, { kind: 'cluster', group, sig: `${group.length}:${radarMode ? 'r' : ''}:${group.map(p => `${p.product?.rate ?? ''}/${radarMode ? p.radar?.rate ?? '' : ''}`).join(',')}` });
    }
    const active = points.find(p => p.id === activeId);
    if (active) wanted.set(`p:${active.id}`, { kind: 'pin', place: active, active: true, sig: `${active.product?.id || ''}:${active.product?.rate ?? ''}:${radarMode ? active.radar?.id || '-' : ''}:1` });

    for (const [key, entry] of markers.current) {
      const want = wanted.get(key);
      if (!want || want.sig !== entry.sig) { entry.marker.remove(); markers.current.delete(key); }
    }
    for (const [key, want] of wanted) {
      if (markers.current.has(key)) continue;
      let element, lngLat;
      if (want.kind === 'pin') {
        element = pinElement(want.place, want.active, radarMode);
        lngLat = [want.place.lng, want.place.lat];
        const place = want.place;
        element.addEventListener('click', e => { e.stopPropagation(); callbacks.current.onSelect?.(place); });
        element.style.zIndex = want.active ? '30' : radarMode && place.radar ? '5' : place.product ? '3' : '1';
      } else {
        const g = want.group;
        lngLat = [g.reduce((s, p) => s + p.lng, 0) / g.length, g.reduce((s, p) => s + p.lat, 0) / g.length];
        element = clusterElement(g, radarMode);
        element.addEventListener('click', e => { e.stopPropagation(); map.current?.easeTo({ center: lngLat, zoom: Math.min(map.current.getZoom() + 2, 18), duration: 400 }, { programmatic: true }); });
        element.style.zIndex = '2';
      }
      const marker = new lib.current.Marker({ element, anchor: 'center' }).setLngLat(lngLat).addTo(map.current);
      markers.current.set(key, { marker, sig: want.sig });
    }
  }, [branches, atms, showAtms, activeId, ready, zoomLevel, radarMode]);

  // 동네 금리 온도: 읍·면·동 면 색칠 (지도 글자 아래에 깔아 이름이 가려지지 않게)
  useEffect(() => {
    const m = map.current;
    if (!ready || !m) return;
    const src = m.getSource('heat');
    if (!heat) {
      ['heat-line', 'heat-fill'].forEach(id => m.getLayer(id) && m.removeLayer(id));
      if (src) m.removeSource('heat');
      return;
    }
    if (src) { src.setData(heat); return; }
    m.addSource('heat', { type: 'geojson', data: heat });
    const before = m.getStyle().layers.find(l => l.type === 'symbol')?.id;
    m.addLayer({ id: 'heat-fill', type: 'fill', source: 'heat', paint: { 'fill-color': ['get', 'color'], 'fill-opacity': ['case', ['==', ['get', 'has'], 1], 0.55, 0.12] } }, before);
    m.addLayer({ id: 'heat-line', type: 'line', source: 'heat', paint: { 'line-color': '#1c6cf6', 'line-opacity': 0.35, 'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.4, 14, 1.4] } }, before);
  }, [heat, ready]);

  // 목록에 마우스를 올린 지점 강조 (안쪽 요소의 클래스만 바꿈)
  useEffect(() => {
    if (!hoverId) return;
    const entry = markers.current.get(`p:${hoverId}`);
    const btn = entry?.marker.getElement().querySelector('.pin');
    if (!btn) return;
    btn.classList.add('is-hover');
    const root = entry.marker.getElement(); const z = root.style.zIndex; root.style.zIndex = '40';
    return () => { btn.classList.remove('is-hover'); root.style.zIndex = z; };
  }, [hoverId]);

  return <div className="mapkit"><div ref={box} className="mapkit-canvas"/>{error && <div className="map-load-error">지도를 불러오지 못했습니다.</div>}<a className="mapkit-logo" href="https://www.maptoolkit.org/" target="_blank" rel="noreferrer"><img src="https://www.maptoolkit.org/assets/maptoolkit-attribution.png" alt="Maptoolkit" loading="lazy"/></a><div className="mapkit-credit"><a href="https://www.maptoolkit.com/copyright" target="_blank" rel="noreferrer">© Maptoolkit</a> <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">© OpenStreetMap</a></div></div>;
}

export default memo(MaptoolkitMap);
